/* eslint-disable camelcase */
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Button,
    Glyphicon,
    Image,
    Link,
    Modal,
    ModalTrigger,
} from 'reaxl';
import { paymentsDuck } from '@/ducks';
import { callToActionAnalytics } from '@/analytics';
import { defaultBudgetPrice } from '@/constants';

const renderDisclaimerModal = (disclaimerOverlayText) => (
    <Modal
        dialogClassName="superHeroDisclaimerModal"
    >
        <Modal.Header closeButton>
            <Modal.Title>
                Disclaimer
            </Modal.Title>
        </Modal.Header>
        <Modal.Body>
            {/* because WordPress adds p tags to the string */}
            <span dangerouslySetInnerHTML={{ __html: disclaimerOverlayText }} />
        </Modal.Body>
    </Modal>
);

function RenderLogo({ logo, destinationUrl, logoOnClick }) {
    if (destinationUrl) {
        return (
            <Link
                data-cmp="heroAdBarLogo"
                label={(
                    <Image
                        className="superHeroLogoImg"
                        src={logo}
                        alt="logo"
                        lazyload={false}
                    />
                )}
                href={destinationUrl || '#'}
                rel="nofollow noopener"
                target="_blank"
                onClick={logoOnClick}
            />
        );
    }
    return (
        <Image
            className="superHeroLogoImg"
            src={logo}
            alt="logo"
            lazyload={false}
        />
    );
}

function VehicleTitle({ vehicleName }) {
    return (
        <div className="enhanced-super-hero-bar-title">
            {vehicleName}
        </div>
    );
}

function VehicleInfoDisclaimer({ disclaimerText, disclaimerOverlayText, disclaimerCta, stopCarousel }) {
    return (
        <div className="display-flex align-items-center">
            {(disclaimerText || disclaimerOverlayText) && (
                <div className="enhanced-super-hero-disclaimer">
                    {disclaimerText && `${disclaimerText} `}
                    {disclaimerOverlayText && (
                        <ModalTrigger
                            modal={renderDisclaimerModal(disclaimerOverlayText)}
                            onClick={() => stopCarousel(true)}
                        >
                            <span id="superHeroAdBarDisclaimerCta">
                                {disclaimerCta}
                            </span>
                        </ModalTrigger>
                    )}
                </div>
            )}
        </div>
    );
}

function FallbackInfoContainer({ fallbackInfo }) {
    return (
        <>
            <div className="enhanced-super-hero-bar-title display-none display-sm-block text-size-sm-400">
                {fallbackInfo.title}
            </div>
            <div className="enhanced-super-hero-bar-subtitle text-size-200 text-size-sm-300">
                {fallbackInfo.subtitle}
            </div>
        </>
    );
}

function AdBarButton(props) {
    const { destinationUrlIsExternal, destinationUrl, ctaText, ctaOnClick } = props;

    return (
        <Button
            data-cmp="heroAdBarCtaButton"
            className="enhanced-super-hero-cta"
            block
            bsStyle="default"
            rel={destinationUrlIsExternal && 'nofollow noopener'}
            target={destinationUrlIsExternal ? '_blank' : '_self'}
            href={destinationUrl || '#'}
            onClick={ctaOnClick}
        >
            <div>
                {ctaText}
                {destinationUrlIsExternal && (
                    <Glyphicon
                        glyph="offsite"
                        className="margin-left-2 super-hero-offsite"
                    />
                )}
            </div>

        </Button>
    );
}

function AdBarMoblieDisclaimerRow({ children }) {
    return (
        <div className="ad-bar-mobile-disclamer-row">
            {children}
        </div>
    );
}

function AdBarGridColoumnOne({ children, hasDisclaimer }) {
    const dots = children[0];
    const bodyText = children[1];
    const disclaimer = children[2];

    return (
        <div className="col-one-ad-bar">
            <div className="col-one-dot-indicator">
                {dots}
            </div>
            <div className="col-one-body-text">
                {bodyText}
            </div>
            {hasDisclaimer && (
                <div className="col-one-disclaimer-text">
                    {disclaimer}
                </div>
            )}
        </div>
    );
}

function AdBarGridColoumnTwo({ children, isMobile }) {
    const ctaButton = children[0];
    const sponsered = children[1];
    const logo = children[2];
    if (isMobile) {
        return (
            <div className="col-two-ad-bar">
                <div className="advertisment">
                    <div className="sponsered">
                        {sponsered}
                    </div>
                    <div className="logo">
                        {logo}
                    </div>
                </div>
                <div className="cta-button">
                    {ctaButton}
                </div>
            </div>
        );
    }
    return (
        <div className="col-two-ad-bar">
            <div className="cta-button">
                {ctaButton}
            </div>
            <div className="advertisment">
                <div className="sponsered">
                    {sponsered}
                </div>
                <div className="logo">
                    {logo}
                </div>
            </div>
        </div>
    );
}

function SuperHeroAdBar({
    superHeroContent,
    isMobile,
    destinationUrl,
    destinationUrlIsExternal,
    handleLogoClick,
    handleButtonClick,
    children: DotIndicator,
    isHomepage,
    stopCarousel,
}) {
    const {
        vehicleName,
        bodyText: {
            disclaimerCta,
            disclaimerText,
            disclaimerOverlayText,
        },
        ctaText,
        advertisementLabel,
        logo,
        useFallback,
        fallbackInfo,
        logoOrientation,
    } = superHeroContent;

    const dispatch = useDispatch();

    let ctaOnClick;
    let logoOnClick;

    const paymentsInfo = useSelector(paymentsDuck.selectors.getPaymentsInfo) || {};

    const hasDisclaimer = !!(disclaimerCta && disclaimerOverlayText) || !!disclaimerText;

    if (useFallback) {
        ctaOnClick = (event) => {
            const { budget } = paymentsInfo;

            if (budget === null || budget === 0 || budget === '0') {
                dispatch(paymentsDuck?.creators?.updatePaymentsInfo({ budget: defaultBudgetPrice }));
            }
            dispatch(paymentsDuck?.creators?.updateMyWallet({ displayModal: true, selectedPanelName: 'budget' }));
            callToActionAnalytics.fireSetMyBudgetClick(event, { text: ctaText, withPixallData: true });
            handleButtonClick(event);
        };
    } else {
        ctaOnClick = (event) => {
            handleButtonClick(event);
        };
        logoOnClick = (event) => {
            handleLogoClick(event);
        };
    }

    return (
        <>
            <style type="text/css">
                {`
                .enhanced-hero-container {
                    .superHeroLogoImg {
                        height: 30px;

                        @media (min-width: 768px) {
                            height: 50px;
                        }

                        @media (max-width: 767px) {
                            ${logoOrientation === 'horizontal' ? 'margin-bottom: -8px;' : ''};
                        }
                    }

                    .advertisment {
                        @media (min-width: 1024px) {
                            ${logoOrientation === 'horizontal' ? 'height: 36px;' : ''};
                        }
                    }
                }
            `}
            </style>
            <div
                className={`hero-ad-bar ${isHomepage ? 'hp' : 'slp'}`}
                data-cmp="superHeroAdBar"
            >
                <div className="enhanced-ad-bar-grid-container">
                    <AdBarGridColoumnOne hasDisclaimer={hasDisclaimer}>
                        {DotIndicator}
                        {useFallback ? (
                            <FallbackInfoContainer
                                fallbackInfo={fallbackInfo}
                            />
                        ) : (
                            <>
                                <VehicleTitle
                                    vehicleName={vehicleName}
                                    useFallBack={useFallback}
                                />
                                {!isMobile && (
                                    <VehicleInfoDisclaimer
                                        disclaimerCta={disclaimerCta}
                                        disclaimerText={disclaimerText}
                                        disclaimerOverlayText={disclaimerOverlayText}
                                        stopCarousel={stopCarousel}
                                    />
                                )}
                            </>
                        )}
                    </AdBarGridColoumnOne>
                    <AdBarGridColoumnTwo isMobile={isMobile}>
                        <AdBarButton
                            destinationUrlIsExternal={destinationUrlIsExternal}
                            destinationUrl={destinationUrl}
                            ctaText={ctaText}
                            ctaOnClick={ctaOnClick}
                        />
                        {advertisementLabel}
                        <RenderLogo
                            logo={logo}
                            destinationUrl={destinationUrl}
                            logoOnClick={logoOnClick}
                        />
                    </AdBarGridColoumnTwo>
                </div>
                {hasDisclaimer && (
                    <AdBarMoblieDisclaimerRow>
                        <VehicleInfoDisclaimer
                            disclaimerCta={disclaimerCta}
                            disclaimerText={disclaimerText}
                            disclaimerOverlayText={disclaimerOverlayText}
                            useFallBack={useFallback}
                            fallbackInfo={fallbackInfo}
                            stopCarousel={stopCarousel}
                        />
                    </AdBarMoblieDisclaimerRow>
                )}
            </div>
        </>
    );
}

export default SuperHeroAdBar;
